"use client";

import type { Select as SelectPrimitive } from "@base-ui/react/select";
import { type TenantContextValue, useTenant } from "@tillix/tenants/react";
import { baseSelectKit } from "./select";
import { conEdisonSelectKit } from "./select.con-edison";
import type { OptionalTenantProp } from "../../../types";
import type {
  BaseSelectItemProps,
  BaseSelectPopupProps,
  BaseSelectTriggerProps,
} from "./select";

export interface SelectParts {
  Root: React.FC<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root>>;
  Trigger: React.FC<BaseSelectTriggerProps>;
  Value: React.FC<SelectPrimitive.Value.Props>;
  Popup: React.FC<BaseSelectPopupProps>;
  Item: React.FC<BaseSelectItemProps>;
  Separator: React.FC<SelectPrimitive.Separator.Props>;
  Group: React.FC<SelectPrimitive.Group.Props>;
  GroupLabel: React.FC<SelectPrimitive.GroupLabel.Props>;
}

function useSelectKit(tenantProp?: TenantContextValue) {
  const contextTenant = useTenant();
  const tenantRefId = tenantProp?.id ?? contextTenant?.id;

  switch (tenantRefId) {
    case "con-edison":
      return conEdisonSelectKit;
    default:
      return baseSelectKit;
  }
}

function makeSelectPart<P extends object>(
  pick: (kit: SelectParts) => React.FC<P>,
) {
  return function SelectPart(props: P & OptionalTenantProp) {
    const { tenant, ...rest } = props;
    const Kit = useSelectKit(tenant);
    const Comp = pick(Kit);
    return <Comp {...(rest as P)} />;
  };
}

export const Select = makeSelectPart<
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root>
>((k) => k.Root) as React.FC<
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root>
> & {
  Root: React.FC<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root>>;
  Trigger: React.FC<BaseSelectTriggerProps>;
  Value: React.FC<SelectPrimitive.Value.Props>;
  Popup: React.FC<BaseSelectPopupProps>;
  Item: React.FC<BaseSelectItemProps>;
  Separator: React.FC<SelectPrimitive.Separator.Props>;
  Group: React.FC<SelectPrimitive.Group.Props>;
  GroupLabel: React.FC<SelectPrimitive.GroupLabel.Props>;
};

Select.Root = makeSelectPart<
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root>
>((k) => k.Root);
Select.Trigger = makeSelectPart<BaseSelectTriggerProps>((k) => k.Trigger);
Select.Value = makeSelectPart<SelectPrimitive.Value.Props>((k) => k.Value);
Select.Popup = makeSelectPart<BaseSelectPopupProps>((k) => k.Popup);
Select.Item = makeSelectPart<BaseSelectItemProps>((k) => k.Item);
Select.Separator = makeSelectPart<SelectPrimitive.Separator.Props>(
  (k) => k.Separator,
);
Select.Group = makeSelectPart<SelectPrimitive.Group.Props>((k) => k.Group);
Select.GroupLabel = makeSelectPart<SelectPrimitive.GroupLabel.Props>(
  (k) => k.GroupLabel,
);
